import { useMutation } from '@apollo/client';
import { useTheme } from '@emotion/react';
import { useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  filterAppointmentsWithinWeek,
  shouldShowAppointmentBookingOnPage,
} from '../../Components/AppointmentBooking/appointmentBooking.helpers';
import {
  getLegalStatementToShow,
  getStateAwareLegalStatement,
  selectCustomerStateCode,
  isLegalStatementValid,
} from '../../Components/LegalStatement/legalStatement.helpers';
import isPhoneStepConversionEnabled from '../../Helpers/phoneStepConversion';
import {
  getDefaultNewsletterName,
  isAnyComponentPendingValidation,
  isEveryComponentValid,
  isLoaderComponent,
} from '../../Components/__helpers__/component.helpers';
import { getPreSubmissionComponents } from '../../Components/__selectors__/component.selectors';
import PreloadStatus from '../../Data/PreloadStatus';
import SubmissionStatus from '../../Data/SubmissionStatus';
import {
  getPageNumbersWithPhoneQuestion,
  getPageNumbersWithVisibleSupplierMatches,
  filterComponentsForPage,
} from '../../Helpers/components';
import {
  exposeFailedForwardPageNavigationEvent,
  exposeCustomerMatchResultsRequestedEvent,
  exposeNavigationControlledEvent,
} from '../../Helpers/events';
import {
  getNavigationControlState,
  NAVIGATION_DIRECTION,
} from '../../Helpers/navigationControl';
import {
  SUBMIT_LEAD_GQL,
  SUBMIT_LEAD_WITH_APPOINTMENT_BOOKING_GQL,
} from '../../Helpers/submission';
import submitLead from '../../Helpers/submitLead';
import { getDynamicMatchSubmissionMetadata } from '../../Helpers/dynamicMatchSubmissionMetadata';
import { setIsNextPageAppointmentBooking } from '../../Lib/redux/slices/appointmentBooking';
import { setIsNextPageCpcCard } from '../../Lib/redux/slices/cpcCard';
import { setShouldRenderForm } from '../../Lib/redux/slices/formConfig';
import {
  setHandleSideEffectsOfLegalStatement,
  setSideEffectDetailsForLegalStatement,
  setStatementOverride,
} from '../../Lib/redux/slices/legalStatement';
import {
  setBlockedDestinationPage,
  setPendingMoveBackwards,
  setPendingMoveForwards,
  setShouldNavigateBackward,
  setShouldNavigateForward,
} from '../../Lib/redux/slices/navigationControls';
import { updateAndExposeDynamicHeightData } from '../../Utils/utils';
import {
  getCustomerMatchesSaga,
  getDynamicNewsletterSaga,
  submitSelectAndConnectAction,
} from '../actionCreators';
import {
  triggerPartialSubmission,
  triggerBsaSubmission,
  determineDestinationPage,
  getEffectiveSubmissionPageNumber,
  isLastPageBeforeSubmission,
  isLegalStatementVisibilityToBeChanged,
  isNavigationBlockedOnPage,
  runPendingSideEffectsOfQuestions,
  startLoadingAppointmentBookingIfApplicable,
  startLoadingOfCpcCardIfApplicable,
  shouldDelayNavigationForSupplierMatch,
  transitionPage,
  validateComponents,
} from './useNavigation.helpers';
import { currentPageContainsStaticLoader } from '../../Components/Page/page.helpers';
import {
  isIntentionallyHidden,
  shouldShowComponent,
} from '../../Helpers/conditionality';
import {
  getTriggerPageForGetCustomerMatchesOrNull,
  SUBCATEGORY_MAX_MATCHES_DEFAULT,
} from '../../Helpers/supplierMatches';
import { Newsletters } from '../../Data/NewsletterBrands';
import useIsForwardNavigationBlockedBySupplierCard from '../useIsForwardNavigationBlockedBySupplierCard/useIsForwardNavigationBlockedBySupplierCard';
import { STATEMENT_OVERRIDES } from '../../Data/Constants';
import { ComponentTypes } from '../../Data/Components';
import { shouldPageTriggerPartialSubmission } from '../../Helpers/partialSubmission';
import { getSelectedSupplierBrands } from '../../Helpers/supplierResolution';
import logToConsole from '../../Utils/logToConsole';
import { isHiddenByProgressiveDisplay } from '../../Helpers/progressiveDisplay';
import { getPostSubmissionQuestionComponentIds } from '../../Helpers/strategyCollection.helpers';
import selectIsPolarisFeatureEnabled from '../../Helpers/polaris';

const useNavigation = () => {
  const { currentPage, isFinalPageBeforeSubmission } = useSelector(
    (store) => store.navigation
  );
  const { pages, partialSubmissionPages } = useSelector(
    (store) => store.paging
  );
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const enableResultsPage = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'enableResultsPage')
  );
  const captureBsaLeadsOnAbandonedFlows = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'captureBsaLeadsOnAbandonedFlows')
  );
  const dynamicSelectAndConnect = useSelector(
    (store) => store.features?.dynamicSelectAndConnect
  );
  const oneToOneSupplierConsent = useSelector((store) =>
    selectIsPolarisFeatureEnabled(store, 'oneToOneSupplierConsent')
  );
  const { appointments } = useSelector((store) => store.appointmentBooking);
  const components = useSelector((store) => store.components);
  const progressiveDisplay = useSelector((store) => store.progressiveDisplay);
  const { parentPageCookies } = useSelector((store) => store.cookies);
  const { questionsWithSideEffectsTriggered } = useSelector(
    (store) => store.events
  );
  const {
    pagesWithNavigationBlocks,
    pendingMoveForwards,
    pendingMoveBackwards,
  } = useSelector((store) => store.navigationControls);
  const { selectedAppointment, bookedAppointmentId } = useSelector(
    (store) => store.appointmentBooking
  );
  const oneTrustLocale = useSelector((store) => store.oneTrustLocale);
  const {
    iFrameId,
    widgetLabel,
    useCidFromURL,
    isDynamicHeightEnabled,
    testMode,
  } = useSelector((store) => store.optionalFormConfig);
  const { preloadStatus: appointmentBookingPreloadStatus } = useSelector(
    (store) => store.appointmentBooking
  );
  const { preloadStatus: cpcCardPreloadStatus } = useSelector(
    (store) => store.cpcCard
  );
  const { eventPublicKeyString } = useSelector((store) => store.publicKeys);
  const twoSeconds = 2000;
  const supplierMatchNavigationDelay = enableResultsPage ? 10750 : 6250;
  const pageNumbers = pages.map((_page, i) => i);
  const {
    countryCode,
    phoneNumberCountryCode,
    formId,
    campaignId,
    sourceSite,
    pageUrl,
    referrerUrl,
    localeCode,
    isDomainInternal,
    submissionPageNumber,
    supplierId,
    privacyPolicy,
    subcategoryName,
    subcategoryId,
    navigationBarButtonTextConfig,
    featureFlags,
  } = useSelector((store) => store.formConfig);
  const formOriginId = process.env.NEXT_PUBLIC_SUBMISSION_ORIGIN_ID;
  const { consentCertification } = useSelector((store) => store.trustedForm);

  const supplierMatches = useSelector((store) => store.supplierMatches);
  const {
    customerMatches: customerBrandMatches,
    subcategoryMatches: subcategoryBrandMatches,
    dynamicMatches,
    matchLevel,
    showCustomerMatchesOnThankYouPage,
    supplierMatchSelectionEnabled,
    subcategoryMaxMatches,
  } = supplierMatches;
  const legalStatement = useSelector((store) => store.legalStatement);
  const strategyCollectionsBySubscriptionId = useSelector(
    (store) => store.strategyCollections?.bySubscriptionId
  );
  const { visitorId } = useSelector((store) => store.userIdentities);
  const { isDynamicNewsletterPresent, dynamicNewsletterName } = useSelector(
    (store) => store.newsletter
  );
  const { themeName } = useSelector((store) => store.theme);
  const privacyPolicyUrl = privacyPolicy?.link || '';

  const { isForwardNavigationBlockedBySupplierCard } =
    useIsForwardNavigationBlockedBySupplierCard();

  const preSubmissionComponents = getPreSubmissionComponents(
    components,
    submissionPageNumber
  );
  const pageComponentIds = pages[currentPage];
  const navigationControlState = getNavigationControlState({
    components,
    pageComponentIds,
  });
  const visibleComponents = {};
  const theme = useTheme();
  const dispatch = useDispatch();
  const [submitLeadMutation] = useMutation(SUBMIT_LEAD_GQL);
  const [submitLeadWithAppointmentBookingMutation] = useMutation(
    SUBMIT_LEAD_WITH_APPOINTMENT_BOOKING_GQL
  );
  const {
    ancestorSid,
    submissionStatus,
    firstSubmissionAttemptTimestamp,
    selectAndConnectBrands,
    latestPartialSubmissionQuestions,
    submissionBlocks,
    bsaSubmissionId,
    bsaSubmissionToken,
  } = useSelector((store) => store.submission);
  const shouldFirePhoneStepConversion = isPhoneStepConversionEnabled({
    oneToOneSupplierConsent,
    captureBsaLeadsOnAbandonedFlows,
  });
  const {
    consentedSuppliersHash,
    isLegalStatementRevealed,
    statementOverride,
  } = legalStatement;
  const customerStateCode = useSelector(selectCustomerStateCode);
  const { statement, consentedLegalStatementHash, supplierLegalStatements } =
    getStateAwareLegalStatement({ legalStatement, customerStateCode });
  const {
    singleSupplierStatement,
    multipleSupplierStatement: multipleSupplierMatchStatement,
    singleSupplierConsentedLegalStatementHash,
    multipleSupplierConsentedLegalStatementHash,
  } = supplierLegalStatements;

  const isSubmissionBlocked = submissionBlocks.length > 0;

  const consentStatement = getLegalStatementToShow({
    components,
    statement,
    supplierId,
    singleSupplierStatement,
    customerMatches: customerBrandMatches,
    multipleSupplierMatchStatement,
    currentPage,
    statementOverride,
    submitButtonText: navigationBarButtonTextConfig?.submit ?? '',
  });

  const pageToTriggerGetCustomerMatches = useMemo(
    () =>
      getTriggerPageForGetCustomerMatchesOrNull({
        components,
        showCustomerMatchesOnThankYouPage,
      }),
    []
  );

  const navigateForward = () => {
    dispatch(setShouldNavigateForward({ shouldNavigateForward: false }));
    if (isForwardNavigationBlockedBySupplierCard) {
      // If the supplier card is visible, we should not allow navigation forward.
      return;
    }
    if (navigationControlState.isForwardBlocked) {
      // Drop any queued move so it cannot replay once the block is lifted
      dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: false }));
      exposeNavigationControlledEvent({
        iFrameId,
        currentPage,
        direction: NAVIGATION_DIRECTION.FORWARD,
        display: navigationControlState.forwardBlockDisplay,
      });
      return;
    }
    // The booking journey's questions live on its own screen, not the page,
    // so they must not hold up the page's validation.
    const postSubmissionQuestionComponentIds =
      getPostSubmissionQuestionComponentIds({ components, pages });
    pageComponentIds.forEach((componentId) => {
      const component = components[`component_${componentId}`];
      const { isVisible } = component;
      if (isIntentionallyHidden(component)) {
        return;
      }
      if (postSubmissionQuestionComponentIds.includes(componentId)) {
        return;
      }
      if (
        isVisible ||
        shouldShowComponent(
          components,
          component,
          dynamicNewsletterName,
          phoneNumberCountryCode,
          subcategoryId,
          dynamicSelectAndConnect
        )
      ) {
        // Current page visible components
        visibleComponents[`component_${componentId}`] =
          components[`component_${componentId}`];
      }
    });

    /**
     * If any of the components are waiting on a saga to resolve then we should not be moving forwards - only move when we have resolved async requests.
     */
    if (isAnyComponentPendingValidation({ components }) === true) {
      dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
      return; // as we shouldn't navigate if we are pendingValidation
    }

    const startLoadingCpcCard = startLoadingOfCpcCardIfApplicable({
      formId,
      preloadStatus: cpcCardPreloadStatus,
      components,
      dispatch,
      destinationPage: currentPage + 1,
    });

    /**
     * If CPC cards are waiting on content to be loaded, do not move forwards
     */
    if (
      startLoadingCpcCard ||
      cpcCardPreloadStatus === PreloadStatus.WAITING_FOR_START ||
      cpcCardPreloadStatus === PreloadStatus.IN_PROGRESS
    ) {
      dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
      return;
    }

    const startLoadingAppointmentBooking =
      startLoadingAppointmentBookingIfApplicable({
        preloadStatus: appointmentBookingPreloadStatus,
        components,
        dispatch,
        destinationPage: currentPage + 1,
        formId,
      });

    /**
     * If Appointment booking is waiting on content to be loaded, do not move forwards
     */
    if (
      startLoadingAppointmentBooking ||
      appointmentBookingPreloadStatus === PreloadStatus.WAITING_FOR_START ||
      appointmentBookingPreloadStatus === PreloadStatus.IN_PROGRESS
    ) {
      dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
      return;
    }

    dispatch(setPendingMoveBackwards({ isAwaitingAsyncRequest: false }));
    dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: false }));

    const progressivePageConfig = progressiveDisplay?.pages?.[currentPage];
    const currentPageComponentsWithValidation = Object.values(
      visibleComponents
    ).filter(
      (component) =>
        component.component.page === currentPage && component.validation
    );
    const currentPageVisibleComponents =
      currentPageComponentsWithValidation.filter(
        (component) =>
          !isHiddenByProgressiveDisplay({
            pageConfig: progressivePageConfig,
            components,
            componentId: component.component.id,
          })
      );
    // A radios chain's hidden questions are not drawn, so they get no error
    // message, but the page cannot move on until they are answered: an Other
    // answer whose text was never committed must not let Next skip them.
    const currentPageRequiredComponents =
      progressivePageConfig?.inputType === ComponentTypes.RADIOS
        ? currentPageComponentsWithValidation
        : currentPageVisibleComponents;

    // TODO: Currently each component is tested against it's validation constraints twice, once for validateComponents to update the store and once during isEveryComponentValid. This is necessary because validate components will take time to update the status of each component following a setComponentValidationStatus dispatch. Therefore the status will be stale throughout the rest of this synchronous function and we cannot use it. isEveryComponentValid therefore checks the components against their constraints again asynchronously. It should be possible to refactor to improve this process. This is covered in the following ticket: https://mvfglobal.atlassian.net/browse/GOLD-567
    validateComponents({
      components: currentPageVisibleComponents,
      countryCode,
      dispatch,
      iFrameId,
      formId,
    });

    if (
      !isEveryComponentValid({
        components: currentPageRequiredComponents,
        countryCode,
      })
    ) {
      exposeFailedForwardPageNavigationEvent({ iFrameId, currentPage });
      return;
    }

    runPendingSideEffectsOfQuestions({
      components: currentPageVisibleComponents,
      questionsWithSideEffectsTriggered,
      dispatch,
    });

    const destinationPageNumber = determineDestinationPage({
      appointments,
      formId,
      pageNumber: currentPage,
      pageNumbers,
      pages,
      components,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId,
      dynamicSelectAndConnect,
      dynamicMatches,
    });

    if (
      isNavigationBlockedOnPage(
        destinationPageNumber,
        pagesWithNavigationBlocks
      )
    ) {
      dispatch(
        setBlockedDestinationPage({
          pageNumber: destinationPageNumber,
        })
      );
      dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
      return;
    }

    const currentPageSelectAndConnectComponent = Object.values(components).find(
      (component) =>
        component.component.page === currentPage &&
        component.component.type === ComponentTypes.SELECT_AND_CONNECT
    );

    /**
     * Navigation waits on the select and connect submission, so it must only do
     * so when the customer's selection actually resolves to brands we can
     * submit. A selection that resolves to nothing has nothing to wait for.
     */
    const selectAndConnectBrandsToSubmit = getSelectedSupplierBrands({
      selectAndConnectComponent: currentPageSelectAndConnectComponent,
      dynamicMatches,
    });

    if (
      currentPageSelectAndConnectComponent &&
      selectAndConnectBrandsToSubmit.length > 0 &&
      Object.values(selectAndConnectBrands).length === 0
    ) {
      dispatch(
        submitSelectAndConnectAction({ selectAndConnectPage: currentPage })
      );
      return;
    }

    /**
     * Set submission in progress so that the legal statement doesn't disappear immediately
     * when we have post submission pages that shouldn't show a legal statement
     */
    let submissionInProgress =
      submissionStatus === SubmissionStatus.IN_PROGRESS;
    if (typeof submissionPageNumber === 'number') {
      if (
        currentPage === submissionPageNumber &&
        submissionStatus !== SubmissionStatus.COMPLETE
      ) {
        submissionInProgress = true;
      }
    }

    /**
     * If the legal consent statement is about to be rendered and dynamic height
     * is enabled, then the resize is required to occur strictly before navigating
     * forwards since it is a footer section resize (NOTE: The footer renders
     * prior to the inputArea due to DOM hierarchy rules).
     * Do not move forwards for now ...
     * After the render and resize are all done, forward movement may begin. The
     * subsequent navigation transition and synchronised height resize then deals
     * with the resize of the inputArea section for the next page (penultimate page).
     */
    const isDestinationPageSubmissionPage = isLastPageBeforeSubmission({
      page: destinationPageNumber,
      components,
      pages,
      submissionPageNumber,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId,
      dynamicSelectAndConnect,
    });

    const supplierMatchPages =
      getPageNumbersWithVisibleSupplierMatches(components);
    const currentPageHasSupplierMatchQuestion =
      supplierMatchPages.includes(currentPage);
    const destinationPageHasSupplierMatchQuestion = supplierMatchPages.includes(
      destinationPageNumber
    );
    const isCurrentPagePartialSubmissionPage =
      shouldPageTriggerPartialSubmission(currentPage, partialSubmissionPages);
    const isDestinationPagePartialSubmissionPage =
      shouldPageTriggerPartialSubmission(
        destinationPageNumber,
        partialSubmissionPages
      );
    const shouldDelayNavigation = shouldDelayNavigationForSupplierMatch({
      destinationPageNumber,
      pages,
      components,
      enableResultsPage,
    });

    // Fire the customerMatchResultsRequested event immediately if needed,
    // before any delay logic, so the results page can start preparing
    if (destinationPageNumber !== currentPage) {
      const destinationPageComponentIds = pages[destinationPageNumber];
      if (
        destinationPageComponentIds &&
        destinationPageComponentIds.length > 0
      ) {
        const destinationPageComponents = filterComponentsForPage(
          components,
          destinationPageComponentIds
        );
        const supplierMatchComponent = Object.values(
          destinationPageComponents
        ).find(
          (component) =>
            component?.component?.type === ComponentTypes.SUPPLIER_MATCH
        );

        if (supplierMatchComponent) {
          // Use customer matches if available, otherwise fall back to subcategory matches
          const matchedBrands =
            customerBrandMatches?.length > 0
              ? customerBrandMatches
              : subcategoryBrandMatches || [];

          if (matchedBrands.length > 0) {
            const componentMaxMatches =
              supplierMatchComponent?.component?.maxMatches ||
              SUBCATEGORY_MAX_MATCHES_DEFAULT;
            const maxMatchesToUse =
              subcategoryMaxMatches ||
              componentMaxMatches ||
              SUBCATEGORY_MAX_MATCHES_DEFAULT;

            exposeCustomerMatchResultsRequestedEvent({
              iFrameId,
              // Cap the relayed list to the top-N here (GOLD-2329) — chameleon-results
              // renders it as-is. NOTE: maxMatchesToUse here also factors in
              // componentMaxMatches. customerMatches stays full.
              matchedBrands: matchedBrands.slice(0, maxMatchesToUse),
              components,
              legalStatement,
              customerStateCode,
              subcategoryName,
              subcategoryId,
              supplierId,
              customerMatches: customerBrandMatches,
              subcategoryMaxMatches: maxMatchesToUse,
              captureBsaLeadsOnAbandonedFlows:
                !!captureBsaLeadsOnAbandonedFlows,
            });
          }
        }
      }
    }

    const {
      isComplexWidgetResizeInProgress,
      blockNavigation,
      timeStampOfBlockNavigationUpdate,
    } = window?.__private__?.dynamicWidgetHeightData || {};
    let isNavigationBlockedByDynamicHeight = false;

    if (isDynamicHeightEnabled && blockNavigation) {
      const howLongHasNavBeenBlockedFor =
        Date.now() - timeStampOfBlockNavigationUpdate;
      if (howLongHasNavBeenBlockedFor >= twoSeconds) {
        updateAndExposeDynamicHeightData({
          blockNavigation: false,
          timeStampOfBlockNavigationUpdate: Date.now(),
        });
        isNavigationBlockedByDynamicHeight = false;
      } else {
        isNavigationBlockedByDynamicHeight = true;
      }
    }

    if (
      !isDestinationPageSubmissionPage &&
      isDestinationPagePartialSubmissionPage &&
      statementOverride !== STATEMENT_OVERRIDES.EMAIL_ONLY
    ) {
      dispatch(
        setStatementOverride({
          statementOverride: STATEMENT_OVERRIDES.EMAIL_ONLY,
        })
      );
    }

    if (
      !isNavigationBlockedByDynamicHeight &&
      isLegalStatementVisibilityToBeChanged({
        isDynamicHeightEnabled,
        statement,
        isDestinationPageSubmissionPage,
        isLegalStatementRevealed,
        destinationPageHasSupplierMatchQuestion,
        currentPageHasSupplierMatchQuestion,
        currentPageIsSubmissionPage: isFinalPageBeforeSubmission,
        submissionInProgress,
        isCurrentPagePartialSubmissionPage,
        isDestinationPagePartialSubmissionPage,
      })
    ) {
      const handleLegalStatementSideEffects = () => {
        batch(() => {
          dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
          dispatch(
            setHandleSideEffectsOfLegalStatement({
              handleSideEffectsOfLegalStatement: true,
            })
          );
          dispatch(
            setSideEffectDetailsForLegalStatement({
              sideEffectDetailsForLegalStatement: {
                destinationPageNumber,
                navigationDirection: 'forwards',
              },
            })
          );
        });
      };

      if (shouldDelayNavigation) {
        setTimeout(
          handleLegalStatementSideEffects,
          supplierMatchNavigationDelay
        );
      } else {
        handleLegalStatementSideEffects();
      }
      return;
    }

    if (isDynamicHeightEnabled && isComplexWidgetResizeInProgress) {
      return;
    }

    if (isDynamicHeightEnabled && isNavigationBlockedByDynamicHeight) {
      if (!pendingMoveForwards) {
        // Postpone this forward navigation attempt until after the block subsides
        updateAndExposeDynamicHeightData({
          pendingNavigationInstruction: 'forward',
        });
        dispatch(setPendingMoveForwards({ isAwaitingAsyncRequest: true }));
      }
      return; // Skip navigation
    }

    if (
      !isDestinationPagePartialSubmissionPage &&
      statementOverride === STATEMENT_OVERRIDES.EMAIL_ONLY
    ) {
      dispatch(setStatementOverride({ statementOverride: null }));
    }

    if (isCurrentPagePartialSubmissionPage && !isFinalPageBeforeSubmission) {
      triggerPartialSubmission({
        preSubmissionComponents,
        currentPage,
        dispatch,
        latestPartialSubmissionQuestions,
      });
    }

    const isCurrentPagePhonePage =
      getPageNumbersWithPhoneQuestion(components).includes(currentPage);

    if (captureBsaLeadsOnAbandonedFlows && isCurrentPagePhonePage) {
      triggerBsaSubmission({ dispatch });
    }

    // When the supplier match at submissionPageNumber does not render — hidden
    // for the whole webform (isComponentHidden), or skipped at runtime because
    // dynamicSelectAndConnect resolved zero options — submitLead must fire on
    // the last visible question page, not on the loader / skipped page that
    // precedes it. The effective submission boundary moves back accordingly.
    // For every other form this returns submissionPageNumber unchanged.
    const effectiveSubmissionPageNumber = getEffectiveSubmissionPageNumber({
      components,
      submissionPageNumber,
      dynamicSelectAndConnect,
    });

    const isTransitionFromPreToPostTransition =
      typeof effectiveSubmissionPageNumber === 'number' &&
      currentPage <= effectiveSubmissionPageNumber &&
      destinationPageNumber > effectiveSubmissionPageNumber;

    if (destinationPageNumber !== currentPage) {
      if (
        pageToTriggerGetCustomerMatches !== null &&
        currentPage <= pageToTriggerGetCustomerMatches &&
        destinationPageNumber > pageToTriggerGetCustomerMatches
      ) {
        dispatch(getCustomerMatchesSaga());
      }

      if (typeof submissionPageNumber !== 'number') {
        const performTransition = () => {
          if (isDynamicHeightEnabled) {
            updateAndExposeDynamicHeightData({
              blockNavigation: true,
              timeStampOfBlockNavigationUpdate: Date.now(),
            });
            logToConsole.debug('[DYNAMIC HEIGHT] NAV BLOCKED 5');
          }

          transitionPage({
            destinationPage: destinationPageNumber,
            currentPage,
            iFrameId,
            components,
            theme,
            dispatch,
            pages,
            submissionPageNumber,
            dynamicNewsletterName,
            phoneNumberCountryCode,
            subcategoryId,
            isDestinationPagePartialSubmissionPage,
            isBounceNavigationAnimationEnabled,
            supplierMatches,
            formId: formId !== undefined ? String(formId) : undefined,
            formOriginId,
            dynamicSelectAndConnect,
          });
        };

        if (shouldDelayNavigation) {
          setTimeout(performTransition, supplierMatchNavigationDelay);
        } else {
          performTransition();
        }

        return;
      }

      if (typeof submissionPageNumber === 'number') {
        if (!isTransitionFromPreToPostTransition) {
          const performTransition = () => {
            if (isDynamicHeightEnabled) {
              updateAndExposeDynamicHeightData({
                blockNavigation: true,
                timeStampOfBlockNavigationUpdate: Date.now(),
              });
              logToConsole.debug('[DYNAMIC HEIGHT] NAV BLOCKED 6');
            }

            transitionPage({
              destinationPage: destinationPageNumber,
              currentPage,
              iFrameId,
              components,
              theme,
              dispatch,
              pages,
              submissionPageNumber,
              dynamicNewsletterName,
              phoneNumberCountryCode,
              subcategoryId,
              isDestinationPagePartialSubmissionPage,
              isBounceNavigationAnimationEnabled,
              supplierMatches,
              formId: formId !== undefined ? String(formId) : undefined,
              formOriginId,
              dynamicSelectAndConnect,
            });
          };

          if (shouldDelayNavigation) {
            setTimeout(performTransition, supplierMatchNavigationDelay);
          } else {
            performTransition();
          }
          return;
        }
      }
    }

    if (
      isSubmissionBlocked ||
      !isLegalStatementValid({
        statement,
        supplierId,
        singleSupplierStatement,
        customerMatches: customerBrandMatches,
        multipleSupplierMatchStatement,
        components,
        currentPage,
        submitButtonText: navigationBarButtonTextConfig?.submit ?? '',
      })
    ) {
      return;
    }

    if (typeof submissionPageNumber === 'number') {
      if (isTransitionFromPreToPostTransition) {
        if (
          submissionStatus === SubmissionStatus.IN_PROGRESS ||
          submissionStatus === SubmissionStatus.AWAITING_RETRY_SUBMISSION
        ) {
          return;
        }

        if (submissionStatus === SubmissionStatus.COMPLETE) {
          const performTransition = () => {
            transitionPage({
              destinationPage: destinationPageNumber,
              currentPage,
              iFrameId,
              components,
              theme,
              dispatch,
              pages,
              submissionPageNumber,
              dynamicNewsletterName,
              phoneNumberCountryCode,
              subcategoryId,
              isDestinationPagePartialSubmissionPage,
              isBounceNavigationAnimationEnabled,
              supplierMatches,
              formId: formId !== undefined ? String(formId) : undefined,
              formOriginId,
              dynamicSelectAndConnect,
            });
          };

          if (shouldDelayNavigation) {
            setTimeout(performTransition, supplierMatchNavigationDelay);
          } else {
            performTransition();
          }
          return;
        }
      }

      if (
        currentPage === destinationPageNumber &&
        submissionStatus === SubmissionStatus.COMPLETE
      ) {
        dispatch(setShouldRenderForm({ shouldRenderForm: false }));
        return;
      }

      // Use the effective submission page so that, when the supplier match
      // is hidden for the whole webform, we don't re-enter the submitLead
      // path after the user has already submitted on the last question.
      if (currentPage > effectiveSubmissionPageNumber) {
        return;
      }
    }

    if (isDynamicNewsletterPresent && !dynamicNewsletterName) {
      const fallbackNewsletterName = getDefaultNewsletterName({ components });
      let fallbackNewsletter;
      if (Object.values(Newsletters).includes(fallbackNewsletterName)) {
        fallbackNewsletter = Object.keys(Newsletters).find(
          (key) => Newsletters[key] === fallbackNewsletterName
        );
      }

      dispatch(
        getDynamicNewsletterSaga({
          visitorId,
          destinationPageNumber,
          fallbackNewsletter,
        })
      );
    }

    const { submissionType, dynamicBrandMatch } =
      getDynamicMatchSubmissionMetadata({
        dynamicMatches,
        brandId: dynamicMatches?.[0]?.brandId,
      });

    submitLead({
      ancestorSid,
      bookedAppointmentId,
      campaignId,
      components: preSubmissionComponents,
      consentedLegalStatementHash,
      consentedSuppliersHash,
      countryCode,
      currentPage,
      dispatch,
      eventPublicKeyString,
      formId,
      iFrameId,
      isDomainInternal,
      localeCode,
      oneTrustLocale,
      pageUrl,
      parentPageCookies,
      phoneNumberCountryCode,
      referrerUrl,
      selectedAppointment,
      sourceSite,
      submitLeadMutation,
      submitLeadWithAppointmentBookingMutation,
      testMode,
      useCidFromURL,
      widgetLabel,
      submissionPageNumber,
      customerBrandMatches,
      subcategoryBrandMatches,
      singleSupplierConsentedLegalStatementHash,
      multipleSupplierConsentedLegalStatementHash,
      consentCertification,
      supplierId,
      visitorId,
      consentStatement,
      privacyPolicyUrl,
      subcategoryName,
      firstSubmissionAttemptTimestamp,
      subcategoryId,
      matchLevel,
      supplierMatchSelectionEnabled,
      themeName,
      featureFlags,
      brandMatchReasons: supplierMatches.brandMatchReasons,
      enableResultsPage,
      submissionType,
      dynamicBrandMatch,
      dynamicSelectAndConnect,
      dynamicMatches,
      oneToOneSupplierConsent,
      bsaSubmissionId,
      bsaSubmissionToken,
      shouldFirePhoneStepConversion,
      strategyCollectionsBySubscriptionId,
    });
  };

  const navigateBackward = () => {
    dispatch(setShouldNavigateBackward({ shouldNavigateBackward: false }));
    if (navigationControlState.isBackwardBlocked) {
      // Drop any queued move so it cannot replay once the block is lifted
      dispatch(setPendingMoveBackwards({ isAwaitingAsyncRequest: false }));
      exposeNavigationControlledEvent({
        iFrameId,
        currentPage,
        direction: NAVIGATION_DIRECTION.BACKWARD,
        display: navigationControlState.backwardBlockDisplay,
      });
      return;
    }
    let destinationPage = currentPage - 1;
    const supplierMatchPages =
      getPageNumbersWithVisibleSupplierMatches(components);
    const currentPageHasSupplierMatchQuestion =
      supplierMatchPages.includes(currentPage);
    const destinationPageHasSupplierMatchQuestion =
      supplierMatchPages.includes(destinationPage);
    const isCurrentPagePartialSubmissionPage =
      shouldPageTriggerPartialSubmission(currentPage, partialSubmissionPages);
    const isDestinationPagePartialSubmissionPage =
      shouldPageTriggerPartialSubmission(
        destinationPage,
        partialSubmissionPages
      );

    const {
      isComplexWidgetResizeInProgress,
      blockNavigation,
      timeStampOfBlockNavigationUpdate,
    } = window?.__private__?.dynamicWidgetHeightData || {};
    let isNavigationBlockedByDynamicHeight = false;

    if (
      isDestinationPagePartialSubmissionPage &&
      statementOverride !== STATEMENT_OVERRIDES.EMAIL_ONLY
    ) {
      dispatch(
        setStatementOverride({
          statementOverride: STATEMENT_OVERRIDES.EMAIL_ONLY,
        })
      );
    }

    if (isDynamicHeightEnabled && blockNavigation) {
      const howLongHasNavBeenBlockedFor =
        Date.now() - timeStampOfBlockNavigationUpdate;
      if (howLongHasNavBeenBlockedFor >= twoSeconds) {
        updateAndExposeDynamicHeightData({
          blockNavigation: false,
          timeStampOfBlockNavigationUpdate: Date.now(),
        });
        isNavigationBlockedByDynamicHeight = false;
      } else {
        isNavigationBlockedByDynamicHeight = true;
      }
    }

    /**
     * If the legal consent statement is about to be unmounted and dynamic height
     * is enabled, then the resize is required to occur strictly before navigating
     * backwards since it is a footer section resize (NOTE: The footer renders
     * prior to the inputArea due to DOM hierarchy rules).
     * Do not move backwards for now ...
     * After the render and resize are all done, forward movement may begin. The
     * subsequent navigation transition and synchronised height resize then deals
     * with the resize of the inputArea section for the next page (penultimate page).
     */
    if (
      !isNavigationBlockedByDynamicHeight &&
      isLegalStatementVisibilityToBeChanged({
        isDynamicHeightEnabled,
        statement,
        isDestinationPageSubmissionPage: false,
        isLegalStatementRevealed,
        destinationPageHasSupplierMatchQuestion,
        currentPageHasSupplierMatchQuestion,
        currentPageIsSubmissionPage:
          isFinalPageBeforeSubmission || isCurrentPagePartialSubmissionPage,
        isCurrentPagePartialSubmissionPage,
        isDestinationPagePartialSubmissionPage,
      })
    ) {
      batch(() => {
        dispatch(setPendingMoveBackwards({ isAwaitingAsyncRequest: true }));
        dispatch(
          setSideEffectDetailsForLegalStatement({
            sideEffectDetailsForLegalStatement: {
              destinationPageNumber: destinationPage,
              navigationDirection: 'backwards',
            },
          })
        );
        dispatch(
          setHandleSideEffectsOfLegalStatement({
            handleSideEffectsOfLegalStatement: true,
          })
        );
      });
      return;
    }

    if (isDynamicHeightEnabled && isComplexWidgetResizeInProgress) {
      return;
    }

    if (isDynamicHeightEnabled && isNavigationBlockedByDynamicHeight) {
      if (!pendingMoveBackwards) {
        // Postpone this backward navigation attempt until after the block subsides
        updateAndExposeDynamicHeightData({
          pendingNavigationInstruction: 'backward',
        });
        dispatch(setPendingMoveBackwards({ isAwaitingAsyncRequest: true }));
      }
      return; // Skip navigation
    }

    while (pageNumbers.includes(destinationPage)) {
      const componentIds = pages[destinationPage];

      const showAppointmentBooking = shouldShowAppointmentBookingOnPage({
        components,
        pageNumber: destinationPage,
        formId,
      });

      const shouldShowPreviousPage = componentIds.some((componentId) => {
        const isLoaderOnPage = currentPageContainsStaticLoader(
          componentIds,
          components
        );

        if (isLoaderOnPage) {
          // We don't need to show a loader while transitioning backwards
          return false;
        }

        if (appointments) {
          const thisWeeksAppointments =
            filterAppointmentsWithinWeek(appointments);

          if (showAppointmentBooking && thisWeeksAppointments?.length === 0) {
            // If there are no appointments in the given time frame, do not show the appointment booking page - which is the next page
            return false;
          }
        }

        return (
          shouldShowComponent(
            components,
            components[`component_${componentId}`],
            dynamicNewsletterName,
            phoneNumberCountryCode,
            subcategoryId,
            dynamicSelectAndConnect
          ) && !isLoaderComponent(components[`component_${componentId}`])
        );
      });

      // Cpc cards are never displayed on a backwards transition
      dispatch(setIsNextPageCpcCard({ isNextPageCpcCard: false }));

      // Appointment booking might be shown when navigating backwards
      const isNextPageAppointmentBooking = shouldShowAppointmentBookingOnPage({
        components,
        pageNumber: destinationPage,
        formId,
      });

      dispatch(
        setIsNextPageAppointmentBooking({
          isNextPageAppointmentBooking,
        })
      );

      const startLoadingAppointmentBooking =
        startLoadingAppointmentBookingIfApplicable({
          preloadStatus: appointmentBookingPreloadStatus,
          components,
          dispatch,
          destinationPage: currentPage - 1,
          formId,
        });

      batch(() => {
        // Cpc cards are never displayed on a backwards transition
        dispatch(setIsNextPageCpcCard({ isNextPageCpcCard: false }));

        dispatch(
          // AppointmentBooking page might be displayed in a backwards transition, but only if there are appointments in given time frame to display
          setIsNextPageAppointmentBooking({
            isNextPageAppointmentBooking: showAppointmentBooking,
          })
        );
      });

      /**
       * If Appointment booking is waiting on content to be loaded, do not move forwards
       */
      if (
        startLoadingAppointmentBooking ||
        appointmentBookingPreloadStatus === PreloadStatus.WAITING_FOR_START ||
        appointmentBookingPreloadStatus === PreloadStatus.IN_PROGRESS
      ) {
        dispatch(setPendingMoveBackwards({ isAwaitingAsyncRequest: true }));
        return;
      }

      dispatch(setPendingMoveBackwards({ isAwaitingAsyncRequest: false }));

      if (
        !isDestinationPagePartialSubmissionPage &&
        statementOverride === STATEMENT_OVERRIDES.EMAIL_ONLY
      ) {
        dispatch(setStatementOverride({ statementOverride: null }));
      }

      if (shouldShowPreviousPage) {
        if (isDynamicHeightEnabled) {
          updateAndExposeDynamicHeightData({
            blockNavigation: true,
            timeStampOfBlockNavigationUpdate: Date.now(),
          });
          logToConsole.debug('[DYNAMIC HEIGHT] NAV BLOCKED 4');
        }

        transitionPage({
          destinationPage,
          currentPage,
          iFrameId,
          components,
          theme,
          dispatch,
          pages,
          submissionPageNumber,
          dynamicNewsletterName,
          phoneNumberCountryCode,
          subcategoryId,
          isDestinationPagePartialSubmissionPage,
          isBounceNavigationAnimationEnabled,
          supplierMatches,
          formId: formId !== undefined ? String(formId) : undefined,
          formOriginId,
          dynamicSelectAndConnect,
        });
        return;
      }

      destinationPage -= 1;
    }
  };

  return {
    navigateForward,
    navigateBackward,
    isForwardBlockedByNavigationControl:
      navigationControlState.isForwardBlocked,
  };
};

export default useNavigation;
